'use client';

import { useTranslation } from 'next-i18next';
import { useEmployerProfileActions } from '@hooks/features/useEmployerProfileActions';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { Button } from '@components/common';
import { HeartIcon as HeartIconOutline, SquaresPlusIcon } from '@heroicons/react/24/outline';

// Interface
// ************************************
export interface ProfileActionsProps {
  workplace: ApiSchema<'WorkplaceProfile'>,
  locale: Locale
}

/**
 * @description Renders the actions for a workplace profile.
 * The following actions are available: follow/unfollow the employer, apply using direct apply.
 *
 * - **workplace** - ApiSchema<'WorkplaceProfile'>. Workplace profile details data.
 * - **locale** - Locale. The current app locale.
 */
export const ProfileActions = (props: ProfileActionsProps) => {
  // Destructure props
  const {
    workplace,
    locale,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Common classes
  const buttonIconClasses = 'mr-2 size-5';

  // Hook: Workplace Profile Actions
  // *****************************************
  const {
    isFollower,
    isTogglingFollower,
    handleToggleFollower,
    handleClaimProfile,
  } = useEmployerProfileActions({
    slug: workplace.slug ?? '',
    name: workplace.name ?? '',
    locale,
    instaJobSlug: workplace.instaJobSlug,
    profileCategories: workplace.profileCategories,
    hasAppliedInitial: !!workplace.appliedAt,
    isFollowerInitial: !!workplace.isFollower,
  });

  // Render component
  // *****************************************
  return (
    <div className="space-y-4">
      {workplace.canFollow && (
        <Button
          styling="soft"
          color="secondary-light"
          rounding="full"
          className="w-full !text-sm"
          onClick={handleToggleFollower}
          isLoading={isTogglingFollower}
        >
          {/* Button icon: only when not in loading state */}
          {!isTogglingFollower && (
            <>
              {isFollower ? (
                <HeartIconSolid className={buttonIconClasses} />
              ) : (
                <HeartIconOutline className={buttonIconClasses} />
              )}
            </>
          )}
          {/* Button text */}
          {isFollower ? t('job.unfollow.company') : t('job.follow.company')}
        </Button>
      )}
      {!workplace.claimed && (
        <Button
          styling="soft"
          color="primary"
          rounding="full"
          className="w-full !text-sm"
          onClick={handleClaimProfile}
        >
          <SquaresPlusIcon className={buttonIconClasses} />
          {t('global.label.claim_profile') }
        </Button>
      )}
      <Button
        styling="soft"
        color="primary"
        rounding="full"
        className="w-full !text-sm md:hidden lg:hidden"
        onClick={() => {
          const el = document.getElementById('workplace-active-jobs');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
          }
        }}
      >
        {t('label.see.company.jobs') }
      </Button>
    </div>
  );
};
